import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Copy, ExternalLink, Loader2, Trash2, Zap, Plus, Play, Sparkles, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("sb_admin_token") || ""}` });

const relTime = (iso) => {
  if (!iso) return "never";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

/**
 * Public share-link manager for a single board.
 * - Create tokens with an expiry
 * - Copy public URL
 * - Revoke
 */
export const ShareModal = ({ slug, onClose }) => {
  const [shares, setShares] = useState([]);
  const [days, setDays] = useState(30);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/boards/${slug}/shares`, { headers: authHeaders() });
      setShares(data.shares || []);
    } catch {
      toast.error("Failed to load share links");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [slug]);

  const create = async () => {
    setCreating(true);
    try {
      await axios.post(
        `${API}/admin/boards/${slug}/share`,
        { days_valid: Math.max(1, Math.min(365, parseInt(days, 10) || 30)), label: label.trim() || null },
        { headers: authHeaders() },
      );
      setLabel("");
      toast.success("Share link created.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this link? Anyone using it will lose access.")) return;
    try {
      await axios.delete(`${API}/admin/boards/${slug}/shares/${token}`, { headers: authHeaders() });
      toast.success("Revoked.");
      load();
    } catch { toast.error("Revoke failed"); }
  };

  const publicUrl = (token) => `${window.location.origin}/board-share/${token}`;

  return (
    <div onClick={onClose} className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="share-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-[#0b0715] border border-white/15 p-6 relative max-h-[85vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/[0.06] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center" data-testid="share-modal-close">
          <X size={14} />
        </button>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#22c55e] mb-1">Share this board</div>
        <div className="font-display font-bold text-xl mb-1">Public read-only link</div>
        <p className="text-[#a395c3] text-xs mb-5">
          Names are obfuscated (first name + initial). No emails, phones, or briefs are exposed.
        </p>

        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 mb-5">
          <div className="grid grid-cols-[1fr_auto] gap-3 mb-3">
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Label (e.g. 'OJ mobile', 'Client demo')"
              className="px-3 py-2 rounded-lg bg-[#05050a] border border-white/15 text-sm focus:outline-none focus:border-[#22c55e]/50"
              data-testid="share-label"
            />
            <input
              type="number"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              min={1} max={365}
              className="w-24 px-3 py-2 rounded-lg bg-[#05050a] border border-white/15 text-sm text-right focus:outline-none focus:border-[#22c55e]/50"
              data-testid="share-days"
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-white/50">Valid for {days} day(s)</span>
            <button
              onClick={create}
              disabled={creating}
              data-testid="share-create-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#22c55e] text-[#05050a] font-semibold text-sm hover:bg-white disabled:opacity-50"
            >
              {creating ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Create link
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin text-[#22c55e]" /></div>
        ) : shares.length === 0 ? (
          <div className="text-sm text-[#a395c3] italic text-center py-6">No share links yet.</div>
        ) : (
          <ul className="space-y-2">
            {shares.map((s) => {
              const expired = new Date(s.expires_at) < new Date();
              const revoked = s.revoked;
              return (
                <li key={s.token} className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`share-row-${s.token}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{s.label || "Unlabeled"}</div>
                      <div className="text-[10px] text-white/45 mt-0.5">
                        {revoked ? "REVOKED" : expired ? "EXPIRED" : `expires ${new Date(s.expires_at).toLocaleDateString()}`}
                        {" · "}{s.hit_count || 0} views
                        {s.last_hit_at && ` · last viewed ${relTime(s.last_hit_at)}`}
                      </div>
                    </div>
                    {!revoked && !expired && (
                      <button
                        onClick={() => revoke(s.token)}
                        data-testid={`share-revoke-${s.token}`}
                        className="w-7 h-7 rounded-full bg-white/[0.05] hover:bg-[#ff0055]/20 hover:text-[#ff5577] flex items-center justify-center text-white/60"
                        title="Revoke"
                      >
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                  {!revoked && !expired && (
                    <div className="flex items-center gap-1">
                      <input
                        readOnly
                        value={publicUrl(s.token)}
                        onClick={(e) => e.target.select()}
                        className="flex-1 px-2 py-1.5 rounded-md bg-[#05050a] border border-white/10 text-[11px] text-white/80 font-mono focus:outline-none"
                        data-testid={`share-url-${s.token}`}
                      />
                      <button
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(publicUrl(s.token));
                            toast.success("Copied.");
                          } catch { toast.error("Copy failed"); }
                        }}
                        title="Copy"
                        data-testid={`share-copy-${s.token}`}
                        className="w-8 h-8 rounded-md bg-white/[0.05] border border-white/10 hover:bg-white/[0.1] flex items-center justify-center flex-shrink-0"
                      >
                        <Copy size={12} />
                      </button>
                      <a
                        href={publicUrl(s.token)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open"
                        className="w-8 h-8 rounded-md bg-white/[0.05] border border-white/10 hover:bg-white/[0.1] flex items-center justify-center flex-shrink-0"
                      >
                        <ExternalLink size={12} />
                      </a>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

/**
 * Automations manager — column-triggered rules (email/push/tag).
 */
const ACTION_LABEL = {
  email: "Send email",
  push: "Send push notification",
  tag: "Auto-tag card",
};
const ACTION_COLOR = { email: "#00f0ff", push: "#22c55e", tag: "#a78bfa" };

export const AutomationsModal = ({ slug, board, onClose }) => {
  const [rows, setRows] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [installingId, setInstallingId] = useState(null);
  const [draft, setDraft] = useState({
    trigger_column: "",
    action_type: "push",
    name: "",
    config: {},
  });

  const load = async () => {
    setLoading(true);
    try {
      const [rulesRes, presetsRes] = await Promise.all([
        axios.get(`${API}/admin/automations`, {
          headers: authHeaders(),
          params: { board_slug: slug },
        }),
        axios.get(`${API}/admin/automations/presets`, {
          headers: authHeaders(),
          params: { board_slug: slug },
        }),
      ]);
      setRows(rulesRes.data.automations || []);
      setPresets(presetsRes.data.presets || []);
    } catch {
      toast.error("Failed to load automations");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [slug]);

  const installPreset = async (preset) => {
    setInstallingId(preset.id);
    try {
      const { data } = await axios.post(
        `${API}/admin/automations/install-preset`,
        null,
        {
          headers: authHeaders(),
          params: { preset_id: preset.id, board_slug: slug },
        },
      );
      if (data.already_installed) {
        toast.info("Already installed on this board.");
      } else {
        toast.success(`Installed: ${preset.name}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Install failed");
    } finally { setInstallingId(null); }
  };

  const installedPresetIds = new Set(rows.filter((r) => r.preset_id).map((r) => r.preset_id));

  const create = async () => {
    if (!draft.trigger_column) return toast.error("Pick a trigger column");
    setCreating(true);
    try {
      await axios.post(
        `${API}/admin/automations`,
        {
          board_slug: slug,
          trigger_column: draft.trigger_column,
          action_type: draft.action_type,
          action_config: draft.config,
          name: draft.name.trim() || null,
        },
        { headers: authHeaders() },
      );
      setDraft({ trigger_column: "", action_type: "push", name: "", config: {} });
      toast.success("Automation created.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const toggle = async (row) => {
    try {
      await axios.patch(`${API}/admin/automations/${row.id}`, { active: !row.active }, { headers: authHeaders() });
      load();
    } catch { toast.error("Toggle failed"); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete automation "${row.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/automations/${row.id}`, { headers: authHeaders() });
      toast.success("Deleted.");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const runTest = async (row) => {
    try {
      const { data } = await axios.post(`${API}/admin/automations/${row.id}/test`, {}, { headers: authHeaders() });
      toast.success(`Test fired: ${data.status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="automations-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-2xl bg-[#0b0715] border border-white/15 p-6 relative max-h-[85vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/[0.06] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center" data-testid="automations-close">
          <X size={14} />
        </button>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#a78bfa] mb-1">Automations</div>
        <div className="font-display font-bold text-xl mb-1">When a card lands in a column…</div>
        <p className="text-[#a395c3] text-xs mb-5">
          Trigger emails, push notifications, or auto-tags when a card is moved to a specific column on this board.
        </p>

        {/* --- Preset library --- */}
        {presets.length > 0 && (
          <div className="rounded-xl border border-[#a78bfa]/25 bg-[#a78bfa]/[0.04] p-4 mb-5" data-testid="automation-presets">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-[#a78bfa]/20 border border-[#a78bfa]/45 text-[#a78bfa] flex items-center justify-center">
                <Sparkles size={13} />
              </div>
              <div>
                <div className="text-sm font-semibold">One-click presets</div>
                <div className="text-[10px] text-white/50">Curated starter rules — installed & editable after.</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {presets.map((p) => {
                const installed = installedPresetIds.has(p.id);
                const color = ACTION_COLOR[p.action_type] || "#a78bfa";
                return (
                  <button
                    key={p.id}
                    onClick={() => !installed && installPreset(p)}
                    disabled={installed || installingId === p.id}
                    data-testid={`preset-${p.id}`}
                    className={`text-left rounded-lg border p-3 transition ${
                      installed
                        ? "border-[#22c55e]/25 bg-[#22c55e]/[0.05] cursor-default"
                        : "border-white/10 bg-white/[0.02] hover:border-[#a78bfa]/40 hover:bg-white/[0.04]"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 rounded-md flex items-center justify-center border flex-shrink-0 mt-0.5"
                        style={{ background: `${color}20`, borderColor: `${color}55`, color }}>
                        <Zap size={10} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold leading-tight truncate">{p.name}</div>
                        <div className="text-[10px] text-white/55 mt-0.5 line-clamp-2">{p.description}</div>
                      </div>
                      {installed ? (
                        <span className="text-[9px] uppercase tracking-[0.2em] font-semibold text-[#22c55e] flex items-center gap-1 flex-shrink-0">
                          <Check size={10} /> On
                        </span>
                      ) : installingId === p.id ? (
                        <Loader2 size={12} className="animate-spin text-[#a78bfa] flex-shrink-0" />
                      ) : (
                        <Plus size={12} className="text-white/50 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* --- New automation form --- */}
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 mb-5">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-[9px] uppercase tracking-[0.24em] text-white/50 mb-1.5">Trigger column</label>
              <select
                value={draft.trigger_column}
                onChange={(e) => setDraft({ ...draft, trigger_column: e.target.value })}
                data-testid="automation-column"
                className="w-full px-3 py-2 rounded-lg bg-[#05050a] border border-white/15 text-sm focus:outline-none focus:border-[#a78bfa]/50"
              >
                <option value="">— pick one —</option>
                {(board?.columns || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[9px] uppercase tracking-[0.24em] text-white/50 mb-1.5">Action</label>
              <select
                value={draft.action_type}
                onChange={(e) => setDraft({ ...draft, action_type: e.target.value, config: {} })}
                data-testid="automation-action"
                className="w-full px-3 py-2 rounded-lg bg-[#05050a] border border-white/15 text-sm focus:outline-none focus:border-[#a78bfa]/50"
              >
                <option value="push">Send push notification</option>
                <option value="email">Send email to card owner</option>
                <option value="tag">Auto-tag the card</option>
              </select>
            </div>
          </div>
          <input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="Rule name (optional)"
            data-testid="automation-name"
            className="w-full px-3 py-2 rounded-lg bg-[#05050a] border border-white/15 text-sm mb-3 focus:outline-none focus:border-[#a78bfa]/50"
          />

          {/* Action-specific config */}
          {draft.action_type === "push" && (
            <div className="grid grid-cols-1 gap-2 mb-3">
              <input
                value={draft.config.title || ""}
                onChange={(e) => setDraft({ ...draft, config: { ...draft.config, title: e.target.value } })}
                placeholder="Push title (e.g. 'Booking confirmed')"
                className="px-3 py-2 rounded-lg bg-[#05050a] border border-white/15 text-sm"
                data-testid="automation-push-title"
              />
              <input
                value={draft.config.body || ""}
                onChange={(e) => setDraft({ ...draft, config: { ...draft.config, body: e.target.value } })}
                placeholder="Push body"
                className="px-3 py-2 rounded-lg bg-[#05050a] border border-white/15 text-sm"
                data-testid="automation-push-body"
              />
            </div>
          )}
          {draft.action_type === "email" && (
            <div className="grid grid-cols-1 gap-2 mb-3">
              <input
                value={draft.config.subject || ""}
                onChange={(e) => setDraft({ ...draft, config: { ...draft.config, subject: e.target.value } })}
                placeholder="Subject (supports {{name}}, {{email}}, {{package_label}})"
                className="px-3 py-2 rounded-lg bg-[#05050a] border border-white/15 text-sm"
                data-testid="automation-email-subject"
              />
              <textarea
                value={draft.config.body_html || ""}
                onChange={(e) => setDraft({ ...draft, config: { ...draft.config, body_html: e.target.value } })}
                placeholder="HTML body — supports {{name}}, {{email}}, {{package_label}}"
                rows={3}
                className="px-3 py-2 rounded-lg bg-[#05050a] border border-white/15 text-sm font-mono"
                data-testid="automation-email-body"
              />
            </div>
          )}
          {draft.action_type === "tag" && (
            <input
              value={draft.config.label || ""}
              onChange={(e) => setDraft({ ...draft, config: { ...draft.config, label: e.target.value } })}
              placeholder="Label to add (e.g. 'hot-lead')"
              className="w-full px-3 py-2 rounded-lg bg-[#05050a] border border-white/15 text-sm mb-3"
              data-testid="automation-tag-label"
            />
          )}
          <button
            onClick={create}
            disabled={creating || !draft.trigger_column}
            data-testid="automation-create"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#a78bfa] text-[#05050a] font-semibold text-sm hover:bg-white disabled:opacity-40"
          >
            {creating ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add rule
          </button>
        </div>

        {/* --- Existing rules --- */}
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin text-[#a78bfa]" /></div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-[#a395c3] italic text-center py-6">No automations on this board yet.</div>
        ) : (
          <ul className="space-y-2">
            {rows.map((r) => {
              const col = (board?.columns || []).find((c) => c.id === r.trigger_column);
              const color = ACTION_COLOR[r.action_type] || "#a78bfa";
              return (
                <li key={r.id} data-testid={`automation-row-${r.id}`}
                  className={`rounded-xl border p-3 ${r.active ? "border-white/10 bg-white/[0.03]" : "border-dashed border-white/10 bg-white/[0.015] opacity-60"}`}>
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center border" style={{ background: `${color}20`, borderColor: `${color}55`, color }}>
                        <Zap size={11} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold">{r.name || "Untitled rule"}</div>
                        <div className="text-[10px] text-white/50">
                          When card enters <b className="text-white/80">{col?.label || r.trigger_column}</b> · {ACTION_LABEL[r.action_type]}
                          {r.run_count > 0 && ` · fired ${r.run_count}× · last ${relTime(r.last_run_at)}`}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => runTest(r)} title="Test fire" data-testid={`automation-test-${r.id}`}
                        className="w-7 h-7 rounded-full bg-white/[0.05] hover:bg-[#22c55e]/20 hover:text-[#22c55e] flex items-center justify-center text-white/60">
                        <Play size={11} />
                      </button>
                      <button onClick={() => toggle(r)} title={r.active ? "Pause" : "Enable"} data-testid={`automation-toggle-${r.id}`}
                        className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full border transition"
                        style={r.active
                          ? { color: "#22c55e", borderColor: "#22c55e55", background: "#22c55e15" }
                          : { color: "#a395c3", borderColor: "rgba(255,255,255,0.15)", background: "transparent" }}>
                        {r.active ? "On" : "Off"}
                      </button>
                      <button onClick={() => del(r)} title="Delete" data-testid={`automation-delete-${r.id}`}
                        className="w-7 h-7 rounded-full bg-white/[0.05] hover:bg-[#ff0055]/20 hover:text-[#ff5577] flex items-center justify-center text-white/60">
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

